import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/request'
import { ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { cn } from '@/lib/utils'

export const thClass = 'px-3 py-2.5 text-left text-eyebrow font-medium uppercase text-ink-muted'
export const tdClass = 'px-3 py-3 align-top text-meta text-ink-body'

/** Loading / not-live / forbidden / error states shared by the admin lists. */
export function ListState({ query, children }: { query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown }; children: ReactNode }) {
  const { t } = useTranslation()
  if (query.isPending) return <LoadingState />
  if (query.isError) {
    const err = query.error
    if (err instanceof ApiError && err.status === 404) return <p className="py-6 text-meta text-ink-muted">{t('common.notAvailable')}</p>
    if (err instanceof ApiError && err.status === 403) return <p className="py-6 text-meta text-ink-muted">{t('common.forbidden')}</p>
    return <ErrorState message={t('common.error', { message: errorMessage(err) })} onRetry={() => void query.refetch()} />
  }
  return <>{children}</>
}

export function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex justify-end gap-3">
      <button type="button" onClick={onEdit} className="text-meta font-medium text-ink hover:underline">
        {t('common.edit')}
      </button>
      <button type="button" onClick={onDelete} className="text-meta font-medium text-status-escalated-fg hover:underline">
        {t('common.delete')}
      </button>
    </div>
  )
}

export function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-block rounded-sm px-2 py-[3px] text-label font-medium',
        active ? 'bg-status-resolved-bg text-status-resolved-fg' : 'bg-status-closed-bg text-status-closed-fg',
      )}
    >
      {active ? t('admin.active') : t('admin.inactive')}
    </span>
  )
}

export function FormError({ error }: { error: unknown }) {
  const { t } = useTranslation()
  if (!error) return null
  const body = error instanceof ApiError ? (error.body as { problems?: unknown } | undefined) : undefined
  const problems = Array.isArray(body?.problems) ? body.problems.map(String) : null
  return (
    <div role="alert" className="col-span-2 rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
      {problems ? (
        <ul className="list-disc pl-5">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : (
        t('common.error', { message: errorMessage(error) })
      )}
    </div>
  )
}
